import { useEffect, useMemo } from "react";
import * as THREE from "three";
import type { SiteConditions } from "../../domain/foundation/siteConditions";
import {
	createGravelGeometry,
	createGravelTexture,
	landscapeStoneMatrices,
} from "./landscapeGeometry";

export function SceneLandscape({
	footprint,
	groundY,
	site,
}: {
	footprint: { width: number; length: number };
	groundY: number;
	site: SiteConditions;
}) {
	const gravel = useMemo(
		() => createGravelGeometry(footprint, groundY, site),
		[footprint, groundY, site],
	);
	const texture = useMemo(() => createGravelTexture(), []);
	const rockGeometry = useMemo(() => new THREE.IcosahedronGeometry(1, 1), []);
	const matrices = useMemo(
		() => landscapeStoneMatrices(footprint, groundY, site),
		[footprint, groundY, site],
	);
	useEffect(() => () => gravel.dispose(), [gravel]);
	useEffect(() => () => texture.dispose(), [texture]);
	useEffect(() => () => rockGeometry.dispose(), [rockGeometry]);
	return (
		<>
			<mesh name="dressing-gravel" geometry={gravel} receiveShadow>
				<meshStandardMaterial
					map={texture}
					roughness={0.96}
					envMapIntensity={0.22}
				/>
			</mesh>
			<instancedMesh
				name="dressing-stones"
				args={[rockGeometry, undefined, matrices.length]}
				receiveShadow
				ref={(mesh) => {
					if (!mesh || typeof mesh.setMatrixAt !== "function") return;
					matrices.forEach((matrix, i) => mesh.setMatrixAt(i, matrix));
					mesh.instanceMatrix.needsUpdate = true;
					mesh.computeBoundingSphere();
				}}
			>
				<meshStandardMaterial
					color={0x77796f}
					roughness={0.92}
					envMapIntensity={0.25}
				/>
			</instancedMesh>
		</>
	);
}
